import moment from "moment";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  useDeleteSolarEstimationMutation,
  useLazyDuplicateEstimationQuery,
  useLazyViewSolarEstimationsAdminQuery,
} from "../../../../redux/api/solar-estimation/solarEstimationAPI";
import { ViewSolarEstimationsRequestDTO } from "../../../../shared/oversight-core/dtos/request-dtos/view-solar-estimations-request-dto";
import { ViewSolarEstimationsResponseDTO } from "../../../../shared/oversight-core/dtos/response-dtos/view-solar-estimations-response-dto";
import { EstimationStatus } from "../../../../shared/oversight-core/enums/estimation-status";
import { EEstimationType } from "../../../../shared/oversight-core/enums/estimation-type";
import { ESolarSolutionType } from "../../../../shared/oversight-core/enums/sola-solution-type";
import { ESolarSimLocalStorage } from "../../../../shared/oversight-core/enums/solar-sim-local-storage";
import { AppRoute } from "../../../../shared/oversight-core/interfaces/app-routes";
import { ISolarEstimationListView } from "../../../../shared/oversight-core/interfaces/solar-estimation-list-view";
import AppDropDown from "../../../../shared/oversight-core/ui-elements/app-dropdown/app-drop-down";
import AppSelect, {
  Option,
} from "../../../../shared/oversight-core/ui-elements/app-select/app-select";
import AppButton from "../../../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import DataTable from "../../../../shared/oversight-core/ui-elements/data-table/data-table";
import MaterialIcon, {
  MtIcon,
} from "../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import Pagination from "../../../../shared/oversight-core/ui-elements/pagination/pagination";
import Search from "../../../../shared/oversight-core/ui-elements/search/search";
import SpinnerModal from "../../../../shared/oversight-core/ui-elements/spinner/spinner";
import {
  getStatusColor,
  getStatusNameUser,
} from "../../../../shared/oversight-core/utils/estimation-status";
import { getSolarEstimationTypeIcon } from "../../../../shared/oversight-core/utils/get-solar-estimation-type-icon";
import Helper from "../../../../shared/oversight-core/utils/helpers";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../../shared/oversight-core/utils/toast";
import EstimationDeleteModal from "../../estimations/components/estimation-delete-modal/estimation-delete-modal";
import EstimationSummaryExportModal from "../../estimations/components/estimation-summary-export-modal/estimation-summary-export-modal";
import PurchaseHistoryModal from "../../estimations/components/purchase-history-modal/purchase-history-modal";
import DuplicateEstimationModal from "../components/duplicate-estimation-modal/duplicate-estimation-modal";

interface IProps {
  selectedEstimate: Option;
  setSelectedEstimate: React.Dispatch<React.SetStateAction<Option>>;
}

const initialFilters: ViewSolarEstimationsRequestDTO = {
  searchValue: "",
  from: "",
  to: "",
  pageNumber: 0,
  pageSize: 20,
};

const estimateOptions: Option[] = [
  { value: "", label: "All Estimation" },
  {
    value: EstimationStatus.SUBMITTED,
    label: "Pending",
  },
  { value: EstimationStatus.APPROVED, label: "Approved" },
  {
    value: EstimationStatus.APPROVED_WITH_CHANGES,
    label: "Approved with Changes",
  },
  { value: EstimationStatus.REJECTED, label: "Rejected" },
  { value: EstimationStatus.PURCHASED, label: "Purchased" },
  { value: EstimationStatus.MY_ESTIMATIONS, label: "My Estimations" },
];

const AdminEstimations = (props: IProps) => {
  const { selectedEstimate, setSelectedEstimate } = props;

  const [searchText, setSearchText] = useState("");
  const [estimations, setEstimations] = useState<ISolarEstimationListView[]>(
    []
  );
  const [totalElements, setTotalElements] = useState(0);
  const [filters, setFilters] = useState({
    ...initialFilters,
  });
  const [updateCurrentState, setUpdateCurrentState] = useState(false);
  const [showDeleteSolarEstimationModal, setShowDeleteSolarEstimationModal] =
    useState(false);
  const [estimationId, setEstimationId] = useState("");
  const [
    showEstimationSummaryExportModal,
    setShowEstimationSummaryExportModal,
  ] = useState(false);
  const [showPurchaseHistoryModal, setShowPurchaseHistoryModal] =
    useState(false);
  const [
    showDuplicateSolarEstimationModal,
    setShowDuplicateSolarEstimationModal,
  ] = useState(false);

  const navigate = useNavigate();
  const [
    triggerSolarEstimationsAdmin,
    { isFetching: isFetchingSolarEstimationAdmin },
  ] = useLazyViewSolarEstimationsAdminQuery();
  const [
    triggerDeleteSolarEstimation,
    { isLoading: isLoadingDeleteSolarEstimation },
  ] = useDeleteSolarEstimationMutation();
  const [
    triggerDuplicateEstimation,
    { isFetching: isFetchingDuplicateEstimation },
  ] = useLazyDuplicateEstimationQuery();

  useEffect(() => {
    const requestParams: ViewSolarEstimationsRequestDTO = {
      pageNumber: filters.pageNumber,
      pageSize: filters.pageSize,
      searchValue: filters.searchValue,
      ascending: true,
      adminEstimations: true,
    };

    triggerSolarEstimationsAdmin({
      ...requestParams,
    })
      .unwrap()
      .then((res: ViewSolarEstimationsResponseDTO) => {
        setTotalElements(res.page.totalElements);
        setEstimations(res.page.elements);
      })
      .catch(() => {
        setEstimations([]);
      });
  }, [triggerSolarEstimationsAdmin, filters, updateCurrentState]);

  const onSearch = () => {
    if (searchText) {
      setFilters({
        ...filters,
        searchValue: searchText,
        pageNumber: 0,
      });
    }
  };

  useEffect(() => {
    if (!searchText) {
      setFilters({ ...initialFilters });
    }
  }, [searchText]);

  const deleteSolarEstimation = (estimationId: string) => {
    triggerDeleteSolarEstimation({ estimationId })
      .unwrap()
      .then(() => {
        showSuccessMessage("Solar estimation deleted successfully");
        setUpdateCurrentState((ps) => !ps);
        setShowDeleteSolarEstimationModal(false);
      })
      .catch(() => {
        showErrorMessage("Failed to delete solar estimation");
      });
  };

  const duplicateSolarEstimation = (estimationId: string) => {
    triggerDuplicateEstimation({ estimationId })
      .unwrap()
      .then(() => {
        showSuccessMessage("Solar estimation duplicated successfully");
        setUpdateCurrentState((ps) => !ps);
        setShowDuplicateSolarEstimationModal(false);
      })
      .catch(() => {
        showErrorMessage("Failed to duplicate solar estimation");
      });
  };

  return (
    <>
      <Row className="gy-3 align-items-center justify-content-between">
        <Col className="col-12 col-lg">
          <Row className="gy-3">
            <Col className="col-12 col-sm-6 col-lg-4 col-xl-3">
              <Search
                onSearch={onSearch}
                handleSearchChange={(searchText) => setSearchText(searchText)}
                placeholder="Search Systems"
                value={searchText}
              />
            </Col>
            <Col className="col-6 col-lg-auto d-none d-sm-block">
              <AppButton
                text="New Estimation"
                className="font-size-14"
                onClick={() => {
                  localStorage.removeItem(
                    ESolarSimLocalStorage.ESTIMATOR_DETAILS_ADVANCED
                  );
                  localStorage.removeItem(
                    ESolarSimLocalStorage.ESTIMATOR_DETAILS_SIMPLIFIED
                  );
                  localStorage.removeItem(
                    ESolarSimLocalStorage.ESTIMATOR_DETAILS_DIRECT
                  );
                  localStorage.removeItem(
                    ESolarSimLocalStorage.ESTIMATOR_DETAILS_WATER_HEATER
                  );
                  localStorage.removeItem("simplifiedSave");
                  localStorage.removeItem("directSave");
                  localStorage.removeItem("waterHeaterSave");
                  navigate(AppRoute.DESIGN_SYSTEM);
                }}
              />
            </Col>
            <Col className="col col-sm col-lg-4 col-xl-3">
              <AppSelect
                options={estimateOptions}
                selectedValue={selectedEstimate}
                onChangeOption={(selectOption) => {
                  setSelectedEstimate(selectOption);
                }}
                className="text-light font-size-14"
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row className="mt-3 d-block d-sm-none">
        <Col>
          <AppButton
            text="New Estimation"
            className="font-size-14"
            onClick={() => {
              localStorage.removeItem(
                ESolarSimLocalStorage.ESTIMATOR_DETAILS_ADVANCED
              );
              localStorage.removeItem(
                ESolarSimLocalStorage.ESTIMATOR_DETAILS_SIMPLIFIED
              );
              localStorage.removeItem(
                ESolarSimLocalStorage.ESTIMATOR_DETAILS_DIRECT
              );
              localStorage.removeItem(
                ESolarSimLocalStorage.ESTIMATOR_DETAILS_WATER_HEATER
              );
              localStorage.removeItem("simplifiedSave");
              localStorage.removeItem("directSave");
              localStorage.removeItem("waterHeaterSave");
              navigate(AppRoute.DESIGN_SYSTEM);
            }}
          />
        </Col>
      </Row>
      <div
        className={`${
          estimations.length > 0 ? `estimation-container pb-4` : `bg-none`
        } mt-4 rounded-2 position-relative`}
      >
        {estimations && estimations.length > 0 ? (
          <>
            <Row className="px-2">
              <Col>
                <DataTable
                  headers={[
                    {
                      id: 1,
                      columnName: "Estimate No.",
                      isSort: true,
                    },
                    {
                      id: 2,
                      columnName: "Title",
                    },
                    {
                      id: 3,
                      columnName: "Date",
                      isSort: true,
                    },
                    {
                      id: 4,
                      columnName: "Type",
                    },
                    {
                      id: 5,
                      columnName: "Energy Need",
                    },
                    {
                      id: 6,
                      columnName: "",
                    },
                  ]}
                  data={estimations.map((i) => {
                    return {
                      data: [
                        <>{i.estimationId || "-"}</>,
                        <>{i.title || "-"}</>,
                        <>{moment(i.createdAt).format("DD/MM/YYYY")}</>,
                        <>
                          {i.estimationType === ESolarSolutionType.BOTH ? (
                            <Row className="row-cols-auto align-items-center gx-1">
                              <Col>
                                <MaterialIcon
                                  icon={
                                    getSolarEstimationTypeIcon(i.estimationType)
                                      .solarPower as MtIcon
                                  }
                                  color="var(--color-system-icon)"
                                  fill
                                  size={18}
                                />
                              </Col>
                              <Col className="font-weight-500">+</Col>
                              <Col>
                                <MaterialIcon
                                  icon={
                                    getSolarEstimationTypeIcon(i.estimationType)
                                      .waterDrop as MtIcon
                                  }
                                  color="var(--color-system-icon)"
                                  fill
                                  size={18}
                                />
                              </Col>
                            </Row>
                          ) : (
                            <MaterialIcon
                              icon={
                                i.estimationType ===
                                ESolarSolutionType.SOLAR_ENERGY_SYSTEM
                                  ? (getSolarEstimationTypeIcon(
                                      i.estimationType
                                    ).solarPower as MtIcon)
                                  : (getSolarEstimationTypeIcon(
                                      i.estimationType
                                    ).waterDrop as MtIcon)
                              }
                              color="var(--color-system-icon)"
                              fill
                              size={18}
                            />
                          )}
                        </>,
                        <>
                          <Row>
                            <Col className="col-5">
                              {i.data?.inputParams?.monthlyRequiredEnergy
                                ? `${Number(
                                    Helper.roundTo2(
                                      i.data.inputParams.monthlyRequiredEnergy
                                    )
                                  )} kWh`
                                : "-"}
                            </Col>
                            <Col
                              className={`col-auto ${
                                i.isDraft
                                  ? "status-draft status-pending-background"
                                  : getStatusColor(i.status)
                              } px-3 py-1 rounded-5`}
                            >
                              {i.isDraft
                                ? `Draft`
                                : getStatusNameUser(i.status)}
                            </Col>
                          </Row>
                        </>,
                        <>
                          <Row className="mx-0 align-items-center">
                            <Col className="px-0">
                              <MaterialIcon
                                icon="visibility"
                                color="var(--color-visibility-icon)"
                                size={15}
                                weight={500}
                                className="cursor-pointer"
                                onClick={() =>
                                  navigate(AppRoute.ESTIMATIONS + "/" + i.id, {
                                    state: {
                                      estimationId: i.id,
                                      isUserView: true,
                                    },
                                  })
                                }
                              />
                            </Col>
                            <Col className="px-0">
                              <AppDropDown
                                items={[
                                  {
                                    text: "Delete Estimation",
                                    onClick: () => {
                                      setShowDeleteSolarEstimationModal(true);
                                      setEstimationId(i.id);
                                    },
                                    disabled:
                                      i.status === EstimationStatus.APPROVED ||
                                      i.status ===
                                        EstimationStatus.APPROVED_WITH_CHANGES ||
                                      i.status === EstimationStatus.PURCHASED ||
                                      i.status === EstimationStatus.SUBMITTED ||
                                      i.status === EstimationStatus.MODIFIED,
                                  },
                                  {
                                    text: "Edit Estimation",
                                    onClick: () => {
                                      if (
                                        i.solarEstimationType ===
                                        EEstimationType.SIMPLIFIED
                                      ) {
                                        localStorage.setItem(
                                          ESolarSimLocalStorage.ESTIMATOR_DETAILS_SIMPLIFIED,
                                          JSON.stringify({
                                            estimationId: i.id,
                                            estimationToken: "token",
                                          })
                                        );

                                        navigate(
                                          AppRoute.DESIGN_SYSTEM_SIMPLIFIED_ASSEMBLE_SYSTEM
                                        );
                                        return;
                                      }

                                      if (
                                        i.solarEstimationType ===
                                        EEstimationType.DIRECT
                                      ) {
                                        localStorage.setItem(
                                          ESolarSimLocalStorage.ESTIMATOR_DETAILS_DIRECT,
                                          JSON.stringify({
                                            estimationId: i.id,
                                            estimationToken: "token",
                                          })
                                        );

                                        navigate(
                                          AppRoute.DESIGN_SYSTEM_DIRECT_ASSEMBLE_SYSTEM
                                        );
                                        return;
                                      }

                                      if (
                                        i.solarEstimationType ===
                                        EEstimationType.ADVANCED
                                      ) {
                                        localStorage.setItem(
                                          ESolarSimLocalStorage.ESTIMATOR_DETAILS_ADVANCED,
                                          JSON.stringify({
                                            estimationId: i.id,
                                            estimationToken: "token",
                                          })
                                        );
                                        navigate(
                                          AppRoute.DESIGN_SYSTEM_ADVANCED_DEVICES,
                                          {
                                            state: {
                                              isNavigateToAssembleSystem: true,
                                            },
                                          }
                                        );
                                        return;
                                      }

                                      localStorage.setItem(
                                        ESolarSimLocalStorage.ESTIMATOR_DETAILS_WATER_HEATER,
                                        JSON.stringify({
                                          estimationId: i.id,
                                          estimationToken: "token",
                                        })
                                      );

                                      navigate(
                                        AppRoute.DESIGN_SYSTEM_WATER_HEATER_ASSEMBLE_SYSTEM
                                      );
                                    },
                                    disabled:
                                      i.status === EstimationStatus.PURCHASED ||
                                      i.status === EstimationStatus.SUBMITTED ||
                                      i.status === EstimationStatus.MODIFIED,
                                  },
                                  {
                                    text: "Export",
                                    onClick: () => {
                                      setShowEstimationSummaryExportModal(true);
                                      setEstimationId(i.id);
                                    },
                                  },
                                  {
                                    text: "Duplicate",
                                    onClick: () => {
                                      setShowDuplicateSolarEstimationModal(
                                        true
                                      );
                                      setEstimationId(i.id);
                                    },
                                  },
                                  // {
                                  //   text: "Purchase History",
                                  //   onClick: () => {
                                  //     setShowPurchaseHistoryModal(true);
                                  //     setUserId(i.user?.id || "");
                                  //   },
                                  // },
                                ]}
                              />
                            </Col>
                          </Row>
                        </>,
                      ],
                    };
                  })}
                />
              </Col>
            </Row>
            <Row className="row-cols-auto align-items-center justify-content-center">
              <Col>
                <Pagination
                  itemsPerPage={filters.pageSize ?? 0}
                  length={totalElements}
                  currentPage={filters.pageNumber + 1}
                  updateCurrentPage={(pn) => {
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      pageNumber: pn - 1,
                    }));
                  }}
                />
              </Col>
              <Col className="mt-4">
                <div
                  className={`country-bg border-2 border-gray-15 rounded-2 d-flex align-items-center justify-content-center text-white py-1 px-3`}
                >
                  <Row className="align-items-center">
                    <Col className="title-4 font-weight-400 font-size-14 col-auto pe-0">
                      {filters.pageSize} items per page
                    </Col>
                    <Col className="ps-0">
                      <AppDropDown
                        items={[
                          {
                            onClick: () =>
                              setFilters({
                                pageNumber: 0,
                                pageSize: 10,
                              }),
                            text: "10 items per page",
                          },
                          {
                            onClick: () =>
                              setFilters({
                                pageNumber: 0,
                                pageSize: 20,
                              }),
                            text: "20 items per page",
                          },
                          {
                            onClick: () =>
                              setFilters({
                                pageNumber: 0,
                                pageSize: 50,
                              }),
                            text: "50 items per page",
                          },
                        ]}
                        icon="arrow_drop_down"
                        showBackgroundAndBorder={false}
                      />
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </>
        ) : (
          <div className="container-dash mt-4">
            <Col className="text-center text-color-2 font-size-12">
              There are no estimations
            </Col>
          </div>
        )}
        <SpinnerModal show={isFetchingSolarEstimationAdmin} />
      </div>
      <EstimationDeleteModal
        show={showDeleteSolarEstimationModal}
        onCancel={() => setShowDeleteSolarEstimationModal(false)}
        onClose={() => setShowDeleteSolarEstimationModal(false)}
        onConfirm={() => {
          if (estimationId) {
            deleteSolarEstimation(estimationId);
          }
        }}
        isLoading={isLoadingDeleteSolarEstimation}
      />
      <DuplicateEstimationModal
        show={showDuplicateSolarEstimationModal}
        onCancel={() => setShowDuplicateSolarEstimationModal(false)}
        onClose={() => setShowDuplicateSolarEstimationModal(false)}
        onConfirm={() => {
          if (estimationId) {
            duplicateSolarEstimation(estimationId);
          }
        }}
        isLoading={isFetchingDuplicateEstimation}
      />
      <EstimationSummaryExportModal
        show={showEstimationSummaryExportModal}
        onCancel={() => setShowEstimationSummaryExportModal(false)}
        onClose={() => setShowEstimationSummaryExportModal(false)}
        estimationId={estimationId}
      />
      <PurchaseHistoryModal
        show={showPurchaseHistoryModal}
        onCancel={() => setShowPurchaseHistoryModal(false)}
        onClose={() => setShowPurchaseHistoryModal(false)}
        userId=""
      />
    </>
  );
};

export default AdminEstimations;
